import { ref, computed } from 'vue'
import { ordersApi } from '../api'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { logger } from '@/core/utils/logger'
import type { Order } from '@/core/types/api'

/**
 * Composable for managing order notes notifications
 * Provides reactive count for notification badge
 */
export function useOrderNotifications() {
  const projectsStore = useProjectsStore()

  const unreadNotesCount = ref(0)
  const ordersWithUnreadNotes = ref<Order[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const hasUnreadNotes = computed(() => {
    return unreadNotesCount.value > 0
  })

  async function fetchUnreadNotesCount() {
    const projectCode = projectsStore.currentProject?.code
    if (!projectCode) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const result = await ordersApi.getOrdersWithUnreadNotes(projectCode)
      unreadNotesCount.value = result.count
      ordersWithUnreadNotes.value = result.orders
    } catch (err: unknown) {
      logger.error('Failed to fetch unread notes count', err)
      error.value = 'Nepodařilo se načíst počet nepřečtených poznámek'
    } finally {
      isLoading.value = false
    }
  }

  async function markAsRead(orderId: string) {
    const projectCode = projectsStore.currentProject?.code
    if (!projectCode) {
      return
    }

    try {
      await ordersApi.markNotesAsRead(orderId, projectCode)
      // Refresh the counts
      await fetchUnreadNotesCount()
    } catch (err: unknown) {
      logger.error('Failed to mark notes as read', err)
      throw err
    }
  }

  // Auto-refresh every 30 seconds
  let refreshInterval: ReturnType<typeof setInterval> | null = null

  function startAutoRefresh(intervalMs: number = 30000) {
    stopAutoRefresh()
    refreshInterval = setInterval(() => {
      fetchUnreadNotesCount()
    }, intervalMs)
  }

  function stopAutoRefresh() {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  return {
    unreadNotesCount,
    ordersWithUnreadNotes,
    hasUnreadNotes,
    isLoading,
    error,
    fetchUnreadNotesCount,
    markAsRead,
    startAutoRefresh,
    stopAutoRefresh
  }
}
